import { PerformanceTracking } from '@freelancer/tracking';
import { ActionReducer } from '@ngrx/store';
import { TypedAction } from './actions';
import { sessionStorageVersions } from './collection-versions';
import { objectFilter } from './helpers';

const STORAGE_KEY = 'datastore_session_store_v17';
const STORAGE_VERSIONS_KEY = 'datastore_session_store_versions_v1';

export function sessionStorageSyncFactory(
  performanceTracking: PerformanceTracking,
) {
  let firstState = true;
  let pendingWrite = false;
  return (reducer: ActionReducer<any, TypedAction>) => (
    state: any,
    action: TypedAction,
  ) => {
    let nextState = state;
    if (firstState) {
      try {
        performanceTracking.mark('read-session-storage-start');
        const sessionStorageState = JSON.parse(
          window.sessionStorage.getItem(STORAGE_KEY) || '{}',
        );
        const cachedSessionStorageVersions = JSON.parse(
          window.sessionStorage.getItem(STORAGE_VERSIONS_KEY) || '{}',
        );
        performanceTracking.mark('read-session-storage-end');
        performanceTracking.measure(
          'read-session-storage',
          'read-session-storage-start',
          'read-session-storage-end',
        );

        /*
         * It is possible that the data saved in session storage is of a
         * different type than is now expected (as the transformed models have
         * changed). Rather than doing JSON Schema validation on the saved
         * storage which would require transmitting a very big JSON file we
         * hash the schemas and if they don't match we ignore that collection.
         */
        const validSessionStorageStates = objectFilter(
          sessionStorageState,
          (key, value) =>
            sessionStorageVersions[key] === cachedSessionStorageVersions[key],
        );

        nextState = {
          ...state,
          ...validSessionStorageStates,
        };
      } catch (e) {
        // ignore the errors, e.g. quota is full or security error
        console.error(e);
      }
    }
    firstState = false;
    const next = reducer(nextState, action);
    if (!pendingWrite) {
      pendingWrite = true;
      window.requestIdleCallback(() => {
        try {
          performanceTracking.mark('write-session-storage-start');
          window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
          window.sessionStorage.setItem(
            STORAGE_VERSIONS_KEY,
            JSON.stringify(sessionStorageVersions),
          );
          performanceTracking.mark('write-session-storage-end');
          performanceTracking.measure(
            'write-session-storage',
            'write-session-storage-start',
            'write-session-storage-end',
          );
        } catch (e) {
          // ignore the errors, e.g. quota is full or security error
          console.error(e);
        }
        pendingWrite = false;
      });
    }

    return next;
  };
}
